import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/chat-model.module.css';

/**
 * Placeholder shown in the chat window while messages for a selected history
 * chat are being fetched. Mirrors the real message layout — avatar + bubble
 * shape — with skeleton lines inside each bubble so the panel reads as a
 * loading conversation, not a generic list of pills.
 *
 * Animation: subtle shimmer via CSS keyframes; suppressed under
 * `prefers-reduced-motion: reduce` in the CSS module.
 */
const ROWS = [
  { side: 'bot', lines: ['82%', '64%'] },
  { side: 'user', lines: ['56%'] },
  { side: 'bot', lines: ['78%', '92%', '48%'] },
  { side: 'user', lines: ['72%', '40%'] },
  { side: 'bot', lines: ['68%', '54%'] },
];

const ChatHistorySkeleton = () => {
  const { t } = useTranslation(['chat']);
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('chat:history.loadingAria', {
        defaultValue: 'Loading conversation',
      })}
      data-testid="chat-history-skeleton"
      className={styles.skeletonContainer}
    >
      {ROWS.map(({ side, lines }, rowIdx) => {
        const isUser = side === 'user';
        return (
          <div
            key={rowIdx}
            className={`${styles.skeletonRow} ${
              isUser ? styles.skeletonRowReverse : ''
            }`}
          >
            <span
              className={`${styles.skeletonAvatar} ${
                isUser ? styles.skeletonAvatarUser : styles.skeletonAvatarBot
              }`}
              aria-hidden="true"
            />
            <div
              className={`${styles.skeletonBubble} ${
                isUser ? styles.skeletonBubbleUser : styles.skeletonBubbleBot
              }`}
            >
              {lines.map((width, lineIdx) => (
                <span
                  key={lineIdx}
                  className={styles.skeletonLine}
                  style={{ width }}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ChatHistorySkeleton;
